import axios from "axios";
import axiosRetry from "axios-retry";
import { getItem, setItem } from "../localStorage";
import { apiUrls } from "./apiUrls";

const apiInstance = () => {
  const getSsoBaseUrl = () => {
    if (process.env.REACT_APP_ENV === "dev") {
      return process.env.REACT_APP_SSO_URL || "http://192.168.1.34:8083/sso-service";
    }
    return process.env.REACT_APP_SSO_URL || "https://kkics.kics.net.in/sso-service";
  };

  const setEnv = () => {
    if (process.env.REACT_APP_ENV === "prod") {
      return process.env.REACT_APP_API_URL;
    } else if (process.env.REACT_APP_ENV === "stag") {
      return process.env.REACT_APP_API_URL;
    } else {
      return process.env.REACT_APP_API_URL;
    }
  };

  let env = setEnv(); // staging

  const api = axios.create({
    baseURL: env,
  });
  axiosRetry(api, {
    retries: 2,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error) => {
      const status = error?.response?.status;
      const method = String(error?.config?.method || "").toLowerCase();
      const url = String(error?.config?.url || "");

      if (status === 401 || status === 403) {
        return false;
      }
      if (method === "post" && url.includes("getAllRecords")) {
        return false;
      }
      if (!["get", "head", "options"].includes(method)) {
        return false;
      }
      return axiosRetry.isNetworkOrIdempotentRequestError(error);
    },
  });

  let refreshPromise = null;

  api.interceptors.request.use(async (config) => {
    const accessToken = getItem("access_token");

    if (config?.url?.includes("sso-service")) {
      // Change base url for SSO service
      config.baseURL = getSsoBaseUrl();
      // Strip /sso-service from the URL path when baseURL already contains it
      if (String(config.baseURL).includes("/sso-service")) {
        config.url = config.url.replace("/sso-service", "");
      }
    } else if (config?.url?.includes("/dms-service/")) {
      // Handle DMS service URLs - strip /dms-service prefix in dev mode since it's in baseURL
      if (process.env.REACT_APP_ENV === "dev") {
        config.url = config.url.replace("/dms-service", "");
      }
    }

    if (accessToken && !config?.url?.includes("access/token")) {
      config.headers["Authorization"] = "Bearer " + accessToken;
    }
    config.headers["deviceId"] = "1";
    return config;
  });

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const refreshToken = getItem("refresh_token");
      const originalRequest = error?.config;
      const isRefreshRequest = String(originalRequest?.url || "").includes("refresh/token");
      if (
        error?.response?.status === 401 &&
        refreshToken &&
        originalRequest &&
        !isRefreshRequest
      ) {
        try {
          if (originalRequest._retry) {
            throw error;
          }
          originalRequest._retry = true;

          if (!refreshPromise) {
            refreshPromise = (async () => {
              let refreshTokenPath = apiUrls.auth.refreshToken;
              const refreshTokenUrl = getSsoBaseUrl();
              if (String(refreshTokenUrl).includes("/sso-service")) {
                refreshTokenPath = refreshTokenPath.replace("/sso-service", "");
              }
              const response = await axios.post(refreshTokenUrl + refreshTokenPath, {
                refresh_token: refreshToken,
              });
              const newAccessToken = response.data.data?.access_token;
              const newRefreshToken = response.data.data?.refresh_token;

              setItem("access_token", newAccessToken);
              setItem("refresh_token", newRefreshToken);
              api.defaults.headers.common.Authorization = "Bearer " + newAccessToken;
              return newAccessToken;
            })().finally(() => {
              refreshPromise = null;
            });
          }

          const newAccessToken = await refreshPromise;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = "Bearer " + newAccessToken;
          return api(originalRequest);
        } catch (refreshError) {
          // console.error("Token refresh failed:", refreshError);
          throw refreshError;
        }
      } else {
        throw error;
      }
    }
  );

  return api;
};

const apiClient = apiInstance();

export default apiClient;
